import { drawPolygon } from '@civilgrid/ui/utils';
import { buffer as turfBuffer } from '@turf/turf';
import chroma from 'chroma-js';
import { nanoid } from 'nanoid';
import type { LineObject } from '~/common/types';
import { BRIGHT_COLORS } from '~/constants/color';
import { calculateLength } from '~/helpers/unitHelpers';
import { type CanvasEvent, type DragEvent, HoverEvent } from '../CanvasEvent';
import CanvasObject from '../CanvasObject';
import { opacityWhileDragging } from '../common';
import type SurfaceBase from '../SurfaceBase';
import type { LngLat, Point } from '../SurfaceBase';
import VertexObject, { type VertexObjectProps } from '../VertexObject';
import { shouldConvertToMiles } from './common';

class EditLine extends CanvasObject {
  p1: VertexObject;
  p2: VertexObject;
  hoverPoint?: Point;

  width: number;

  midPoint?: Point;

  constructor(id: string, surface: SurfaceBase, p1: VertexObject, p2: VertexObject, width: number) {
    super(id, surface);
    this.p1 = p1;
    this.p2 = p2;
    this.sortOrder = 'a1';
    this.width = width;

    this.on('hovering', (e) => {
      this.hoverPoint = e.point;
      this.renderAll();
      this.surface.setCursor('crosshair');
    });
    this.on('hoverEnd', () => {
      this.hoverPoint = undefined;
      this.renderAll();
      this.surface.resetCursor();
    });
  }

  refreshScreenCoords(): void {
    // the vertices will take care of this
  }

  drawHitArea(ctx: OffscreenCanvasRenderingContext2D, color: string): void {
    const line = this.getCurrentLineObject();
    if (!line) return;
    ctx.beginPath();
    ctx.strokeStyle = color;
    ctx.lineWidth = this.width * 2;
    ctx.moveTo(this.p1.point.x, this.p1.point.y);
    ctx.lineTo(this.p2.point.x, this.p2.point.y);
    ctx.stroke();
  }

  getCurrentLineObject() {
    const currentObject = this.getStore().currentObject;
    if (currentObject?.type !== 'line') return;
    return currentObject;
  }

  render(ctx: CanvasRenderingContext2D): void {
    const line = this.getCurrentLineObject();
    if (!line) return;
    const { properties } = line;
    if (this.hoverPoint) {
      ctx.beginPath();
      ctx.setLineDash([]);
      ctx.arc(this.hoverPoint.x, this.hoverPoint.y, 10, 0, 2 * Math.PI);
      ctx.fillStyle = 'white';
      ctx.strokeStyle = properties.color;
      ctx.fill();
      ctx.stroke();
    }
    ctx.beginPath();
    ctx.setLineDash([]);
    const isDashed = properties.strokeStyle === 'dashed';
    // if the line is dashed, set the line to be dashed
    isDashed && ctx.setLineDash([10, 4]);

    if (this.midPoint) {
      ctx.setLineDash([]);
      ctx.globalAlpha = properties.strokeOpacity;
      ctx.strokeStyle = properties.color;
      ctx.moveTo(this.p1.point.x, this.p1.point.y);
      ctx.lineTo(this.midPoint.x, this.midPoint.y);
      ctx.lineTo(this.p2.point.x, this.p2.point.y);
      ctx.stroke();
    } else {
      ctx.globalAlpha = properties.strokeOpacity;
      ctx.strokeStyle = properties.color;
      ctx.moveTo(this.p1.point.x, this.p1.point.y);
      ctx.lineTo(this.p2.point.x, this.p2.point.y);
      ctx.stroke();
      // This resets the line dash to ensure the point is NOT also dashed, just the line.
      ctx.setLineDash([]);
    }
  }
}

export default class EditLineObject extends CanvasObject {
  dragOffset: Point = { x: NaN, y: NaN };
  vertices: VertexObject[] = [];
  lines: EditLine[] = [];
  _onLineUpdated: (o: LngLat[]) => void;
  pointerVertex: VertexObject | null = null;

  constructor(id: string, surface: SurfaceBase, onPolygonUpdated: (p: LngLat[]) => void) {
    super(id, surface);
    this._onLineUpdated = onPolygonUpdated;
    this.interactive = true;

    const line = this.getCurrentLineObject();
    if (!line) throw new Error('currentObject is not a line!');

    // exclude last coordinate since its just the first
    this.vertices = line.geometry.coordinates.map((c) => {
      const lngLat = {
        lng: c[0],
        lat: c[1],
      };

      return this.createVertex({
        lngLat,
        point: this.surface.toScreenCoords(lngLat),
      });
    });
    this.createLines();

    this.on('dragStart', () => {
      // hide all the vertices and the lines when dragging
      this.vertices.forEach((v) => {
        v.visible = false;
      });
      this.removeLines();
      this.resetDragOffset();
      this.renderAll();
    });
    this.on('dragging', (e) => {
      this.dragOffset = e.diff();
      this.renderAll();
    });
    this.on('dragEnd', () => {
      this.vertices.forEach((v) => {
        v.point.x += this.dragOffset.x;
        v.point.y += this.dragOffset.y;
        v.refreshMapCoords();
        v.visible = true;
      });
      this.createLines();
      this.resetDragOffset();
      this.onPolygonUpdated();
      this.renderAll();
    });
  }

  onPolygonUpdated = () => {
    this._onLineUpdated([...this.vertices].map((v) => v.lngLat));
  };

  createVertex(_v: VertexObjectProps) {
    const v = new VertexObject(nanoid(), this.surface, _v);
    v.interactive = true;
    v.on('dragging', (e) => this.updateVertex(v, e));
    v.on('dragEnd', () => this.onPolygonUpdated());
    v.on('tap', () => {
      const index = this.vertices.findIndex(({ id }) => id === v.id);

      this.surface.disableInteractionEvents();
      const popup = window.document.createElement('div');
      popup.className = 'min-w-[8em] bg-white cursor-pointer border';
      popup.style.position = 'absolute';
      popup.style.top = `${v.point.y + 10}px`;
      popup.style.left = `${v.point.x + 10}px`;

      if (this.vertices.length > 2) {
        const deleteItem = window.document.createElement('div');
        deleteItem.className = 'px-4 py-3 border-b hover:bg-brand-light';
        deleteItem.innerText = 'Delete';
        deleteItem.onclick = () => {
          this.vertices.splice(index, 1);
          v.destroy();
          this.removeLines();
          this.createLines();
          this.surface.enableInteractionEvents();
          this.renderAll();
          this.onPolygonUpdated();
          dismiss();
        };
        popup.appendChild(deleteItem);
      }

      // can extend, if end vertex
      if (index === 0 || index === this.vertices.length - 1) {
        const extendItem = window.document.createElement('div');
        extendItem.className = 'px-4 py-3 border-b hover:bg-brand-light';
        extendItem.innerText = 'Extend';
        extendItem.onclick = () => {
          // should reverse since we're extending from the first point
          if (index === 0) {
            this.vertices.reverse();
          }

          this.pointerVertex = new VertexObject(`pointer-${this.id}`, this.surface, {
            point: { x: NaN, y: NaN },
            lngLat: { lng: NaN, lat: NaN },
          });

          this.addChild(this.pointerVertex);
          this.surface.addListener('hovering', (e) => {
            this.pointerVertex?.setCoords(e);
            this.surface.renderAll();
          });

          // make all vertices non-interactive
          this.interactive = false;
          this.vertices.forEach((v) => {
            v.interactive = false;
          });
          this.removeLines();
          this.createLines();

          // goto to draw mode
          dismiss();
        };

        popup.appendChild(extendItem);
      }

      this.surface.canvas.parentElement!.appendChild(popup);

      const dismiss = () => {
        this.surface.enableInteractionEvents();
        popup.remove();
        v.emit(
          'hoverEnd',
          new HoverEvent('hoverEnd', {
            point: { x: 0, y: 0 },
            lngLat: { lng: 0, lat: 0 },
          }),
        );
        this.surface.canvas.removeEventListener('mouseup', dismiss);
      };
      this.surface.canvas.addEventListener('mouseup', dismiss);
    });
    this.addChild(v);
    return v;
  }

  get length() {
    const store = this.getStore();
    const { currentUnitSystem: unitSystem } = store;

    let vertices = this.vertices;

    if (this.pointerVertex && !Number.isNaN(this.pointerVertex.point.x)) {
      vertices = [...vertices, this.pointerVertex];
    }

    const geometry: GeoJSON.LineString = {
      type: 'LineString',
      coordinates: [...vertices.map((v) => v.getLngLatAsArray())],
    };

    let l = calculateLength({ unitSystem, geometry });

    if (shouldConvertToMiles({ store, line: l })) {
      l = calculateLength({ unitSystem, unitType: 'miles', geometry });
    }

    return l;
  }

  resetDragOffset = () => {
    this.dragOffset = { x: NaN, y: NaN };
  };

  updateVertex = (v: VertexObject, e: DragEvent) => {
    this.removeLines();
    v.point = e.currentPoint;
    v.lngLat = e.currentLngLat;
    this.createLines();
    this.renderAll();
  };

  onSurfaceTap = (e: CanvasEvent) => {
    if (this.pointerVertex) {
      if (e.type === 'dblTap') {
        // done drawing.
        this.pointerVertex.destroy();
        this.pointerVertex = null;

        this.removeLines();
        this.createLines();

        this.vertices.map((v) => (v.interactive = true));

        return;
      }

      const newVertex = this.createVertex(e);
      newVertex.interactive = false;

      this.vertices.push(newVertex);
      this.createLines();
      this.onPolygonUpdated();
      this.renderAll();
    } else {
      this.getStore().resetCurrentObjectId();
    }
  };

  createLines = () => {
    const currentLineObject = this.getCurrentLineObject();
    if (!currentLineObject) return;
    const { properties } = currentLineObject;
    const allVertices = [...this.vertices];
    // draw the lines
    for (let i = 0; i < allVertices.length - 1; i++) {
      const line = new EditLine(
        `l-${i}-${this.id}`,
        this.surface,
        allVertices[i],
        allVertices[i + 1],
        properties.strokeWidth,
      );
      // mid point creation has started
      line.on('dragStart', (e) => {
        const v = this.createVertex(e);
        this.vertices.splice(i + 1, 0, v);
        v.on('dragging', (e) => this.updateVertex(v, e));
      });
      // mid point is being moved
      line.on('dragging', (e) => {
        this.removeLines();
        const v = this.vertices[i + 1];
        v.point = e.currentPoint;
        v.lngLat = e.currentLngLat;
        this.createLines();
        this.renderAll();
      });
      // mid point creation has ended
      line.on('dragEnd', () => this.onPolygonUpdated());

      if (this.pointerVertex) {
        line.interactive = false;
      } else {
        line.interactive = true;
      }

      this.addChild(line);
      this.lines.push(line);
    }
  };

  removeLines = () => {
    this.lines.forEach((line) => {
      this.removeChild(line);
    });
    this.lines = [];
  };

  isDragging = () => {
    return !Number.isNaN(this.dragOffset.x);
  };

  getLabelLocationData() {
    let p1 = this.vertices[0].point;
    let p2 = this.vertices[1].point;
    let maxLength = 0;

    for (let i = 0; i < this.vertices.length - 1; i++) {
      const xDiff = this.vertices[i + 1].point.x - this.vertices[i].point.x;
      const yDiff = this.vertices[i + 1].point.y - this.vertices[i].point.y;
      const sqrt = Math.sqrt(xDiff * xDiff + yDiff * yDiff);
      if (sqrt > maxLength) {
        p1 = this.vertices[i].point;
        p2 = this.vertices[i + 1].point;
        maxLength = sqrt;
      }
    }

    const lineLength = maxLength;
    const labelPoint = {
      x: p1.x + (p2.x - p1.x) / 2,
      y: p1.y + (p2.y - p1.y) / 2,
    };

    const rawRotation = Math.atan2(p2.y - p1.y, p2.x - p1.x);
    let rotation: number;
    if (rawRotation >= Math.PI / 2 && rawRotation <= Math.PI) {
      rotation = -1 * Math.PI + rawRotation;
    } else if (rawRotation > -1 * Math.PI && rawRotation < (-1 * Math.PI) / 2) {
      rotation = Math.PI + rawRotation;
    } else {
      rotation = rawRotation;
    }

    return { labelPoint, rotation, lineLength };
  }

  getCurrentLineObject() {
    const currentObject = this.getStore().currentObject;
    if (currentObject?.type !== 'line') return;
    return currentObject;
  }

  getBufferedPolygonScreenCoords(line: LineObject) {
    const { properties } = line;

    const bufferedPolygon = turfBuffer(
      {
        type: 'LineString',
        coordinates: this.vertices.map(({ lngLat }) => [lngLat.lng, lngLat.lat]),
      },
      properties.buffer || 4 / 2 ** (this.surface.map.getZoom() - 20),
      {
        units: 'feet',
      },
    )?.geometry as GeoJSON.Polygon;

    return bufferedPolygon.coordinates.map((polygon) =>
      polygon.map(([lng, lat]) => this.surface.toScreenCoords({ lng, lat })),
    );
  }

  drawBoundingBox(ctx: CanvasRenderingContext2D) {
    const line = this.getCurrentLineObject();
    if (!line) return;
    const { properties } = line;

    const bbox = this.getBufferedPolygonScreenCoords(line);
    ctx.globalAlpha = 1;
    ctx.setLineDash([]);
    ctx.beginPath();
    ctx.strokeStyle = properties.color;
    ctx.lineWidth = 1;
    ctx.fillStyle = properties.color;

    drawPolygon(bbox, ctx);

    ctx.stroke();
  }

  drawBoundBoxHitArea(ctx: OffscreenCanvasRenderingContext2D, color: string) {
    const line = this.getCurrentLineObject();
    if (!line) return;
    const bbox = this.getBufferedPolygonScreenCoords(line);

    ctx.fillStyle = color;

    ctx.beginPath();

    drawPolygon(bbox, ctx);
    ctx.stroke();
    ctx.fill();
  }

  calculateTextPoint = (textWidth: number): Point => {
    const offset = 25;
    const lastPoint = this.vertices[this.vertices.length - 1].point;
    const cursorPoint = this.pointerVertex!.point;

    if (cursorPoint.x >= lastPoint.x) {
      return { x: cursorPoint.x + offset, y: cursorPoint.y };
    } else {
      return { x: cursorPoint.x - textWidth - offset, y: cursorPoint.y };
    }
  };

  render(ctx: CanvasRenderingContext2D) {
    const line = this.getCurrentLineObject();
    if (!line) return;
    const { properties, title } = line;

    const padding = 10;
    const fontSize = 14;
    const labelOffset = 2 * padding;

    // set global transparency to be 1 (fully opaque)
    ctx.globalAlpha = 1;

    if (this.pointerVertex) {
      ctx.beginPath();
      // if the line is dashed, set the line to be dashed
      ctx.setLineDash([10, 4]);
      ctx.globalAlpha = 1;
      ctx.strokeStyle = 'red';
      const lastVertex = this.vertices[this.vertices.length - 1];
      ctx.moveTo(lastVertex.point.x, lastVertex.point.y);
      ctx.lineTo(this.pointerVertex.point.x, this.pointerVertex.point.y);
      ctx.stroke();
      ctx.setLineDash([]);

      // render length text
      const l = this.length;

      ctx.font = `bold ${fontSize}px arial`;
      const lengthText = `${l.value.toLocaleString()} ${l.unit}`;
      const textWidth = ctx.measureText(lengthText).width;
      const textPoint = this.calculateTextPoint(textWidth);
      ctx.fillStyle = 'red';
      ctx.beginPath();
      ctx.roundRect(textPoint.x, textPoint.y, textWidth + padding, fontSize + padding, [5]);
      ctx.fill();
      ctx.textBaseline = 'top';
      ctx.fillStyle = 'white';
      ctx.fillText(lengthText, textPoint.x + padding / 2, textPoint.y + padding / 2);

      return;
    }

    // the object is currently being moved
    if (this.isDragging()) {
      if (this.vertices.length > 0) {
        // if dragging, set opacity to be 30% transparent.
        ctx.globalAlpha = properties.strokeOpacity;

        if (ctx.globalAlpha > opacityWhileDragging) {
          ctx.globalAlpha = opacityWhileDragging;
        }

        const isDashed = properties.strokeStyle === 'dashed';
        const drawLine = (p1: VertexObject, p2: VertexObject) => {
          ctx.strokeStyle = properties.strokeStyle;
          ctx.beginPath();
          isDashed && ctx.setLineDash([10, 4]);
          ctx.moveTo(p1.point.x + this.dragOffset.x, p1.point.y + this.dragOffset.y);
          ctx.lineTo(p2.point.x + this.dragOffset.x, p2.point.y + this.dragOffset.y);
          ctx.stroke();

          ctx.setLineDash([]);
        };

        for (let i = 0; i < this.vertices.length - 1; i++) {
          drawLine(this.vertices[i], this.vertices[i + 1]);
        }
      }
      return;
    }

    // Note: vertices are rendered by the object
    // lines
    if (this.vertices.length > 0) {
      // draw bounding box if more than 1 vertex.
      this.drawBoundingBox(ctx);

      if (!properties.showLength && !properties.showTitle) {
        return;
      }

      const l = this.length;

      ctx.font = `bold ${fontSize}px arial`;
      const lengthText = `${l.value.toLocaleString()} ${l.unit}`;
      const lengthTextWidth = ctx.measureText(lengthText).width;
      const titleTextWidth = ctx.measureText(title).width;
      const { labelPoint, rotation, lineLength } = this.getLabelLocationData();

      let labelText: string;
      let textWidth: number;
      ctx.lineWidth = properties.strokeWidth;

      if (title === '' || (properties.showLength && !properties.showTitle)) {
        labelText = lengthText;
        textWidth = lengthTextWidth;
      } else if (properties.showTitle && !properties.showLength) {
        labelText = title;
        textWidth = titleTextWidth;
      } else {
        labelText = `${title}\n${lengthText}`;
        textWidth = Math.max(lengthTextWidth, titleTextWidth);
      }

      // draw buffer before text to preserve z-index
      if (properties.buffer) {
        const bufferedPolygon = this.getBufferedPolygonScreenCoords(line);
        ctx.beginPath();
        ctx.strokeStyle = properties.color;
        ctx.fillStyle = chroma(properties.color).alpha(0.25).hex();
        drawPolygon(bufferedPolygon, ctx);

        ctx.stroke();
        ctx.fill();
      }

      if (properties.labelAlwaysVisible || textWidth <= lineLength) {
        if (labelText !== '' && textWidth !== 0) {
          const lines = labelText.trim() === '' ? ['Empty Text'] : labelText.split('\n');
          const textHeight = fontSize * lines.length;
          ctx.save();
          ctx.globalAlpha = 1;
          ctx.translate(labelPoint.x, labelPoint.y);
          ctx.rotate(rotation);
          ctx.fillStyle = properties.color;
          ctx.beginPath();
          ctx.roundRect(
            0 - textWidth / 2 - padding / 2,
            0 - textHeight - padding - labelOffset,
            textWidth + padding,
            textHeight + padding + (lines.length - 1) * padding,
            [5],
          );
          ctx.fill();

          ctx.textBaseline = 'top';
          ctx.textAlign = 'center';
          ctx.fillStyle = BRIGHT_COLORS.includes(properties.color) ? '#000000' : '#FFFFFF';
          const x = 0;
          const y = 0 - textHeight - padding / 2 - labelOffset;
          for (let i = 0; i < lines.length; i++) {
            ctx.fillText(lines[i], x, y + i * fontSize + (i !== 0 ? padding : 0));
          }
          ctx.restore();
        }
      }
    }
  }

  drawHitArea(ctx: OffscreenCanvasRenderingContext2D, color: string): void {
    const line = this.getCurrentLineObject();
    if (!line) return;
    if (this.isDragging()) {
      if (this.vertices.length >= 2) {
        ctx.beginPath();
        const firstPoint = this.vertices[0];
        ctx.moveTo(this.dragOffset.x + firstPoint.point.x, this.dragOffset.y + firstPoint.point.y);

        for (let i = 1; i < this.vertices.length; i++) {
          const p = this.vertices[i];
          ctx.lineTo(this.dragOffset.x + p.point.x, this.dragOffset.y + p.point.y);
        }
      }
      return;
    }

    // bounding box hit area rendering
    this.drawBoundBoxHitArea(ctx, color);
  }
}
